import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
} from 'recharts';
import type {
  RevenueByMonth,
  QuotesByStatus,
  RevenueByLob,
  MarginBucket,
} from '../types/analytics.types';
import { formatCurrency, formatNumber } from '@/lib/utils/format';

const STATUS_COLORS: Record<string, string> = {
  DRAFT: '#94a3b8',
  PENDING_APPROVAL: '#f59e0b',
  APPROVED: '#10b981',
  REJECTED: '#ef4444',
  FROZEN: '#6366f1',
  EXPIRED: '#9ca3af',
  ADJUSTMENT_REQUESTED: '#3b82f6',
};

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Borrador',
  PENDING_APPROVAL: 'Pendiente',
  APPROVED: 'Aprobada',
  REJECTED: 'Rechazada',
  FROZEN: 'Congelada',
  EXPIRED: 'Expirada',
  ADJUSTMENT_REQUESTED: 'Ajuste',
};

const LOB_COLORS = [
  '#6366f1',
  '#8b5cf6',
  '#a78bfa',
  '#c4b5fd',
  '#ddd6fe',
  '#10b981',
  '#f59e0b',
  '#ef4444',
];
const MARGIN_COLORS = ['#ef4444', '#f59e0b', '#eab308', '#22c55e', '#10b981', '#059669'];

interface ChartsSectionProps {
  revenueByMonth: RevenueByMonth[];
  quotesByStatus: QuotesByStatus[];
  revenueByLob: RevenueByLob[];
  marginDistribution: MarginBucket[];
}

const tooltipStyle = {
  backgroundColor: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '8px',
  color: 'hsl(var(--foreground))',
};

function formatMonthLabel(month: string): string {
  const [year, m] = month.split('-');
  const months = [
    'Ene',
    'Feb',
    'Mar',
    'Abr',
    'May',
    'Jun',
    'Jul',
    'Ago',
    'Sep',
    'Oct',
    'Nov',
    'Dic',
  ];
  return `${months[parseInt(m, 10) - 1]} ${year.slice(2)}`;
}

export function ChartsSection({
  revenueByMonth,
  quotesByStatus,
  revenueByLob,
  marginDistribution,
}: ChartsSectionProps) {
  const revenueData = revenueByMonth.map((d) => ({
    ...d,
    label: formatMonthLabel(d.month),
  }));

  const statusData = quotesByStatus
    .filter((d) => d.count > 0)
    .map((d) => ({
      name: STATUS_LABELS[d.status] || d.status,
      value: d.count,
      color: STATUS_COLORS[d.status] || '#94a3b8',
    }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Revenue Over Time */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Ingresos Aprobados por Mes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            {revenueData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12 }}
                    className="fill-muted-foreground"
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    className="fill-muted-foreground"
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={
                      ((value: any) => [formatCurrency(Number(value ?? 0)), 'Ingresos']) as any
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#6366f1"
                    strokeWidth={2}
                    fill="url(#revenueGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                Sin datos de ingresos para este período
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quotes by Status */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Cotizaciones por Estado</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                Sin cotizaciones en este período
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Revenue by LOB */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Ingresos por Línea de Negocio</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            {revenueByLob.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueByLob} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 12 }}
                    className="fill-muted-foreground"
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                  />
                  <YAxis
                    type="category"
                    dataKey="lobName"
                    tick={{ fontSize: 11 }}
                    className="fill-muted-foreground"
                    width={120}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={
                      ((value: any) => [formatCurrency(Number(value ?? 0)), 'Ingresos']) as any
                    }
                  />
                  <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                    {revenueByLob.map((_, index) => (
                      <Cell key={index} fill={LOB_COLORS[index % LOB_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                Sin datos de LOB para este período
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Margin Distribution */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Distribución de Márgenes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            {marginDistribution.some((d) => d.count > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={marginDistribution}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12 }}
                    className="fill-muted-foreground"
                  />
                  <YAxis tick={{ fontSize: 12 }} className="fill-muted-foreground" />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={
                      ((value: any) => [formatNumber(Number(value ?? 0), 0), 'Cotizaciones']) as any
                    }
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {marginDistribution.map((_, index) => (
                      <Cell key={index} fill={MARGIN_COLORS[index % MARGIN_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                Sin datos de márgenes para este período
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
